import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Users, Lock } from "lucide-react";
import { api } from "../lib/api";
import { Page, Loader, Empty, Card } from "../components/UI";
import { MatchStatusBadge, DuelRow } from "../components/Arena";
import { SimBadge } from "../components/Badges";
import { ClubCrest } from "../components/ClubCrest";

export default function MatchControl() {
  const { matchId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  const load = useCallback(() => {
    api.get(`/matches/${matchId}/control`).then(({ data }) => setData(data)).catch(() => setErr(true));
  }, [matchId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  if (err) return <Page><Empty label="Not authorized or match not found" /></Page>;
  if (!data) return <Loader />;

  return (
    <Page>
      <Link to={`/match/${data.id}`} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#E5B842] mb-4" style={{ transition: "color 0.15s ease" }}>
        <ArrowLeft className="w-4 h-4" /> Back to match
      </Link>
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#E5B842] font-mono font-bold mb-1">Match Control Center</div>
          <h1 className="font-heading text-4xl font-black uppercase tracking-tight text-slate-50">{data.public_match_id}</h1>
          <p className="text-slate-500 text-xs font-mono mt-1 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Read-only · server is authoritative · opponent coupons stay sealed</p>
        </div>
        <MatchStatusBadge status={data.status} />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-8">
        {[["HOME", data.home_club], ["AWAY", data.away_club]].map(([side, club]) => {
          const t = data.teams[side];
          return (
            <Card key={side} className="p-5" data-testid={`control-team-${side}`}>
              <div className="flex items-center gap-3 mb-4">
                <ClubCrest club={club} size={44} />
                <div className="flex-1">
                  <div className="font-heading font-bold uppercase text-slate-50">{club?.name}</div>
                  <div className="font-mono text-xs text-slate-400">{side}</div>
                </div>
                <div className="font-heading text-3xl font-black text-[#E5B842] tabular-nums">{t.count}<span className="text-sm text-slate-500">/{t.max}</span></div>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-2 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Per-player usage (max 3)</div>
              <div className="space-y-1.5" data-testid={`usage-${side}`}>
                {t.player_usage.length === 0 ? (
                  <div className="text-slate-600 text-sm">No coupons yet.</div>
                ) : t.player_usage.map((u) => (
                  <div key={u.player?.player_id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="text-slate-300 truncate flex items-center gap-1.5">
                      {u.player?.display_name}{u.player?.is_simulated && <SimBadge />}
                    </span>
                    <span className={`font-mono font-bold ${u.at_max ? "text-rose-400" : "text-slate-400"}`}>
                      {u.at_max ? "MAX 3/3" : `${u.count}/3`}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>

      <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50 mb-4">Duel States</h2>
      <div className="grid md:grid-cols-2 gap-3">
        {data.duels.map((slot) => <DuelRow key={slot.sequence_number} slot={slot} />)}
      </div>
    </Page>
  );
}
